import React, { useState, useEffect } from 'react';
import { 
  Home, 
  Search, 
  Library, 
  Heart, 
  History,
  Music,
  Plus,
  Volume2,
  ChevronRight,
  Disc3,
  ListMusic
} from 'lucide-react';
import dataManager from '../utils/dataManager';
import CreatePlaylistDialog from './CreatePlaylistDialog';

const Sidebar = ({ currentPage, onPageChange, onNavigateToLibrary, librarySelectedPlaylist, userPlaylists, onPlaylistsChange }) => {
  const [isPlaylistExpanded, setIsPlaylistExpanded] = useState(true);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);

  const menuItems = [
    { id: 'home', label: '首页', icon: Home },
    { id: 'search', label: '搜索', icon: Search },
    { id: 'library', label: '音乐库', icon: Library },
    { id: 'queue', label: '播放队列', icon: ListMusic }
  ];

  const libraryItems = [
    { id: 'favorites', label: '我喜欢的音乐', icon: Heart },
    { id: 'recent', label: '最近播放', icon: History }
  ];

  // 创建新歌单
  const handleCreatePlaylist = (name) => {
    dataManager.createPlaylist(name);
    if (onPlaylistsChange) {
      onPlaylistsChange(); // 通知父组件刷新歌单列表
    }
  };

  const MenuItem = ({ item, isActive, onClick }) => {
    const Icon = item.icon;
    return (
      <button
        onClick={() => onClick(item.id)}
        className={`w-full flex items-center gap-4 px-6 py-3 text-left transition-all duration-200 group hover:bg-dark-100 ${
          isActive 
            ? 'bg-dark-100 text-primary-400 border-r-2 border-primary-400' 
            : 'text-dark-500 hover:text-dark-700'
        }`}
      >
        <Icon 
          size={20} 
          className={`transition-transform duration-200 ${
            isActive ? 'scale-110' : 'group-hover:scale-105'
          }`} 
        />
        <span className="font-medium">{item.label}</span>
      </button>
    );
  };

  const LibraryItem = ({ item, isActive, onClick }) => {
    const Icon = item.icon;
    return (
      <button
        onClick={() => onClick(item.id)}
        className={`w-full flex items-center gap-3 px-8 py-2.5 text-left transition-all duration-200 group hover:bg-dark-100 ${
          isActive 
            ? 'bg-dark-100 text-primary-400' 
            : 'text-dark-400 hover:text-dark-600'
        }`}
      >
        <Icon size={16} className="group-hover:scale-105 transition-transform duration-200" />
        <span className="text-sm font-medium">{item.label}</span>
      </button>
    );
  };

  const PlaylistItem = ({ playlist, isActive, onClick }) => {
    return (
      <button
        onClick={() => onClick(playlist.id)}
        className={`w-full flex items-center gap-3 px-8 py-2.5 text-left transition-all duration-200 group hover:bg-dark-100 ${
          isActive 
            ? 'bg-dark-100 text-primary-400' 
            : 'text-dark-400 hover:text-dark-600'
        }`}
      >
        <Disc3 size={16} className="group-hover:scale-105 transition-transform duration-200" />
        <div className="flex-1 min-w-0">
          <div className="text-sm font-medium truncate">{playlist.name}</div>
          <div className="text-xs text-dark-300">{playlist.songCount} 首歌曲</div>
        </div>
      </button>
    );
  };

  return (
    <div className="w-64 bg-dark-50 border-r border-dark-200 flex flex-col h-full">
      {/* Logo区域 */}
      <div className="p-6 border-b border-dark-200">
        <div className="flex items-center gap-3">
          <img 
            src="/logo.png" 
            alt="TuneFree Logo" 
            className="w-8 h-8 object-contain"
          />
          <div>
            <h1 className="text-lg font-bold text-dark-800">TuneFree</h1>
            <p className="text-xs text-dark-400">Next Generation</p>
          </div>
        </div>
      </div>

      {/* 滚动内容区域 */}
      <div className="flex-1 overflow-y-auto scrollbar-thin">
        {/* 主要导航 */}
        <div className="py-4">
          {menuItems.map((item) => (
            <MenuItem
              key={item.id}
              item={item}
              isActive={currentPage === item.id || (item.id === 'queue' && currentPage === 'queue')}
              onClick={onPageChange}
            />
          ))}
        </div>

        {/* 音乐库分割线 */}
        <div className="border-t border-dark-200 my-2"></div>

        {/* 音乐库 */}
        <div className="py-2">
          <div className="px-6 mb-3">
            <h3 className="text-xs font-semibold text-dark-300 uppercase tracking-wider">
              音乐库
            </h3>
          </div>
          {libraryItems.map((item) => (
            <LibraryItem
              key={item.id}
              item={item}
              isActive={currentPage === 'library' && librarySelectedPlaylist === item.id}
              onClick={() => onNavigateToLibrary(item.id)}
            />
          ))}
        </div>

        {/* 歌单列表分割线 */}
        <div className="border-t border-dark-200 my-2"></div>

        {/* 歌单列表 */}
          <div className="py-2">
            <div className="flex items-center justify-between px-6 mb-3">
              <h3 className="text-xs font-semibold text-dark-300 uppercase tracking-wider">
                歌单列表
              </h3>
              <div className="flex items-center gap-1">
                <button
                  onClick={() => setIsPlaylistExpanded(!isPlaylistExpanded)}
                  className="p-1 hover:bg-dark-100 rounded transition-colors duration-200"
                >
                  <ChevronRight 
                    size={14} 
                    className={`text-dark-400 transition-transform duration-200 ${
                      isPlaylistExpanded ? 'rotate-90' : ''
                    }`} 
                  />
                </button>
                                   <button
                     onClick={() => setCreateDialogOpen(true)}
                     className="p-1 hover:bg-dark-100 rounded transition-colors duration-200"
                     title="创建歌单"
                   >
                     <Plus size={14} className="text-dark-400 hover:text-primary-400 transition-colors duration-200" />
                   </button>
              </div>
            </div>
            
            {isPlaylistExpanded && (
              <div className="space-y-1">
                {userPlaylists.length > 0 ? (
                  userPlaylists.map((playlist) => (
                    <PlaylistItem
                      key={playlist.id}
                      playlist={{
                        id: playlist.id,
                        name: playlist.name,
                        songCount: playlist.songs?.length || 0
                      }}
                      isActive={currentPage === 'library' && librarySelectedPlaylist === playlist.id}
                      onClick={() => onNavigateToLibrary(playlist.id)}
                    />
                  ))
                ) : (
                  <div className="px-8 py-4 text-center">
                    <p className="text-xs text-dark-400">还没有歌单</p>
                                         <button
                       onClick={() => setCreateDialogOpen(true)}
                       className="text-xs text-primary-500 hover:text-primary-600 mt-1"
                     >
                       创建第一个歌单
                     </button>
                  </div>
                )}
              </div>
            )}
          </div>
      </div>

      {/* 底部用户信息或设置 */}
      <div className="border-t border-dark-200 p-4">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 bg-gradient-to-br from-primary-500 to-primary-700 rounded-full flex items-center justify-center">
            <span className="text-xs font-semibold text-white">游</span>
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-sm font-medium text-dark-700 truncate">游客</div>
            <div className="text-xs text-dark-400">免费版本</div>
          </div>
        </div>

        {/* 创建歌单对话框 */}
        <CreatePlaylistDialog
          isOpen={createDialogOpen}
          onClose={() => setCreateDialogOpen(false)}
          onConfirm={handleCreatePlaylist}
        />
      </div>
    </div>
  );
};

export default Sidebar;
